import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  Activity,
  CircleOff,
  Dna,
  Microscope,
  Scan,
  Sparkles,
  Syringe,
  TestTube2,
  type LucideIcon,
} from "lucide-react-native";
import {
  QuestionLayout,
  MedicalHistoryTextField,
  RectangularOptionButton,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useFertilityTesting } from "../FertilityTestingContext";
import { buildNavigationStack, type TestType } from "../getNextTestScreen";
import type { FertilityTestingStackParamList } from "../FertilityTestingNavigator";
import { useFertilityTestingUpload } from "../useFertilityTestingUpload";
import type { TestResultCreate } from "@louise/medical-history/basic-info/types";
import { isFertilityTestingResult } from "../isFertilityTestingResult";

const testTypes: {
  id: TestType;
  labelKey: string;
  icon: LucideIcon;
}[] = [
  {
    id: "urine",
    labelKey: "med_history.fertility.testing.type.urine",
    icon: TestTube2,
  },
  {
    id: "imaging",
    labelKey: "med_history.fertility.testing.type.imaging",
    icon: Scan,
  },
  {
    id: "sampling",
    labelKey: "med_history.fertility.testing.type.sampling",
    icon: Microscope,
  },
  {
    id: "genetic",
    labelKey: "med_history.fertility.testing.type.genetic",
    icon: Dna,
  },
  {
    id: "blood",
    labelKey: "med_history.fertility.testing.type.blood",
    icon: Syringe,
  },
  {
    id: "semen",
    labelKey: "med_history.fertility.testing.type.semen",
    icon: Activity,
  },
  {
    id: "other",
    labelKey: "med_history.fertility.testing.type.other",
    icon: Sparkles,
  },
];

function matchesTestType(result: TestResultCreate, type: TestType): boolean {
  switch (type) {
    case "urine":
      return result.type === "urine";
    case "imaging":
      return result.type === "imaging" && isFertilityTestingResult(result);
    case "sampling":
      return result.type === "sampling";
    case "genetic":
      return result.type === "genetics";
    case "blood":
      return result.type === "blood";
    case "semen":
      return result.type === "semen_fluid";
    case "other":
      return (
        result.type === "clinical" && result.clinicalTestCategory === "other"
      );
  }
}

export function FertilityTestingTypesScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTesting();
  const { uploadAction, uploadModal, uploadedFiles } =
    useFertilityTestingUpload();
  const navigation =
    useNavigation<NavigationProp<FertilityTestingStackParamList>>();

  const selected = new Set<TestType>((state.selectedTypes ?? []) as TestType[]);
  const noneSelected = Boolean(state.noneSelected);

  const toggleSelection = (type: TestType) => {
    const newSelection = new Set(selected);

    if (newSelection.has(type)) {
      newSelection.delete(type);

      const idsToDelete = state.testResults
        .filter((result) => matchesTestType(result, type) && result.id)
        .map((result) => result.id!);
      const filteredResults = state.testResults.filter(
        (result) => !matchesTestType(result, type),
      );

      updateState({
        selectedTypes: Array.from(newSelection),
        hasTestingDone: newSelection.size > 0,
        noneSelected: false,
        otherTestingNotes:
          type === "other" ? null : (state.otherTestingNotes ?? null),
        testResultIdsToDelete: Array.from(
          new Set([...state.testResultIdsToDelete, ...idsToDelete]),
        ),
        testResults: filteredResults,
      });
      return;
    }

    newSelection.add(type);
    let updatedResults = state.testResults;

    if (
      type === "other" &&
      !state.testResults.some((result) => matchesTestType(result, type))
    ) {
      updatedResults = [
        ...updatedResults,
        {
          type: "clinical",
          clinicalTestCategory: "other",
          notes: state.otherTestingNotes ?? null,
        },
      ];
    }

    updateState({
      selectedTypes: Array.from(newSelection),
      hasTestingDone: true,
      noneSelected: false,
      testResults: updatedResults,
    });
  };

  const toggleNone = () => {
    if (noneSelected) {
      updateState({ noneSelected: false, hasTestingDone: null });
      return;
    }

    const idsToDelete = state.testResults
      .filter((result) => isFertilityTestingResult(result) && result.id)
      .map((result) => result.id!);

    updateState({
      hasTestingDone: false,
      noneSelected: true,
      otherTestingNotes: null,
      selectedTypes: [],
      testResultIdsToDelete: Array.from(
        new Set([...state.testResultIdsToDelete, ...idsToDelete]),
      ),
      testResults: state.testResults.filter(
        (result) => !isFertilityTestingResult(result),
      ),
    });
  };

  const handleOtherNotesChange = (notes: string) => {
    updateState({
      otherTestingNotes: notes,
      testResults: state.testResults.map((result) =>
        matchesTestType(result, "other") ? { ...result, notes } : result,
      ),
    });
  };

  const handleSave = () => {
    const selectedArray = Array.from(selected);
    const navigationStack = buildNavigationStack(selectedArray);
    updateState({
      hasTestingDone: noneSelected ? false : true,
      navigationStack,
      currentNavigationIndex: 0,
    });
    const nextScreen = navigationStack[0];
    navigation.navigate(nextScreen as keyof FertilityTestingStackParamList);
  };

  return (
    <>
      <QuestionLayout
        title={t("med_history.fertility.testing.types_title")}
        subtitle={t("med_history.fertility.testing.types_subtitle")}
        onBack={() => navigation.goBack()}
        onSave={handleSave}
        saveDisabled={selected.size === 0 && !noneSelected}
        saveLabel={t("med_history.fertility.continue")}
        secondaryAction={uploadAction}
        uploadedFiles={uploadedFiles}
      >
        <View style={styles.optionsGrid}>
          {testTypes.map((test) => {
            const isSelected = selected.has(test.id);
            return (
              <View key={test.id} style={styles.optionWrapper}>
                <SquareOptionButton
                  icon={test.icon}
                  label={t(test.labelKey)}
                  isSelected={isSelected}
                  onPress={() => toggleSelection(test.id)}
                  iconSize={20}
                  containerSize={40}
                  labelSize="sm"
                />
              </View>
            );
          })}
        </View>

        <RectangularOptionButton
          icon={CircleOff}
          label={t("med_history.fertility.testing.none")}
          isSelected={noneSelected}
          onPress={toggleNone}
          iconSize={19}
        />

        {selected.has("other") && (
          <View style={styles.otherDetails}>
            <MedicalHistoryTextField
              label={t("med_history.fertility.testing.other_details_label")}
              accessibilityLabel={t(
                "med_history.fertility.testing.other_details_label",
              )}
              multiline
              onChangeText={handleOtherNotesChange}
              placeholder={t(
                "med_history.fertility.testing.other_details_placeholder",
              )}
              textAlignVertical="top"
              value={state.otherTestingNotes ?? ""}
            />
          </View>
        )}
      </QuestionLayout>
      {uploadModal}
    </>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
  otherDetails: {
    gap: 8,
    marginTop: 12,
  },
});

export default FertilityTestingTypesScreen;
